import type { BoardVisual } from '../types';

export type BoardRenderer = 'svg' | 'pixi' | 'three';
export const RENDERER_ORDER: readonly BoardRenderer[] = ['three', 'pixi', 'svg'];
export const RENDERER_LABELS: Record<BoardRenderer, string> = { three: '3D', pixi: 'Pixi 2D', svg: 'SVG' };

/** Only a presentation's explicit capability/runtime opt-in changes the viewer's default. */
export function rendererPreference(enabled: boolean, key: string, storage?: Pick<Storage, 'getItem'>): 'svg' | 'pixi' {
  if (!enabled) return 'svg';
  try { const saved = storage?.getItem(key); if (saved === 'svg' || saved === 'pixi') return saved; } catch { /* optional viewer storage */ }
  return 'pixi';
}

/**
 * Renderers one visual can use, preferred first. 3D needs the visual's three block and the three-board capability;
 * Pixi needs a pixi or three runtime plus pixi-board (a three-only visual without pixi-board falls straight to SVG).
 */
export function availableRenderers(visual: Pick<BoardVisual, 'runtime'> | undefined, requires: readonly string[]): BoardRenderer[] {
  const runtime = visual?.runtime, out: BoardRenderer[] = [];
  if (runtime?.three && requires.includes('three-board')) out.push('three');
  if (runtime && requires.includes('pixi-board')) out.push('pixi');
  out.push('svg');
  return out;
}
/** A saved viewer choice wins when it is available here, then the visual's declared renderer, then the best available. */
export function resolveRenderer(available: readonly BoardRenderer[], declared: BoardRenderer | undefined, saved: string | null | undefined): BoardRenderer {
  if (saved && available.includes(saved as BoardRenderer)) return saved as BoardRenderer;
  if (declared && available.includes(declared)) return declared;
  return available[0] ?? 'svg';
}
/** Fallback order three, pixi, svg from the preferred renderer, skipping unavailable or failed ones. */
export function activeRenderer(preferred: BoardRenderer, available: readonly BoardRenderer[], failed: ReadonlySet<BoardRenderer>): BoardRenderer {
  for (const candidate of RENDERER_ORDER.slice(RENDERER_ORDER.indexOf(preferred))) if (available.includes(candidate) && !failed.has(candidate)) return candidate;
  return 'svg';
}
/** The viewer-wide choice for a whole presentation: the union of what its visuals offer and the first declared default. */
export function presentationRenderers(visuals: readonly Pick<BoardVisual, 'runtime'>[] | undefined, requires: readonly string[]): { available: BoardRenderer[]; declared: BoardRenderer } {
  const lists = (visuals ?? []).filter(v => v.runtime).map(v => availableRenderers(v, requires));
  const available = RENDERER_ORDER.filter(r => lists.some(list => list.includes(r)));
  const first = (visuals ?? []).find(v => v.runtime)?.runtime?.renderer;
  return { available: available.length ? [...available] : ['svg'], declared: first === 'three' ? 'three' : 'pixi' };
}
export function readSavedRenderer(key: string, storage?: Pick<Storage, 'getItem'>): BoardRenderer | null {
  try { const value = (storage ?? localStorage).getItem(key); return value === 'svg' || value === 'pixi' || value === 'three' ? value : null; } catch { return null; }
}
